import { useState, type ReactNode } from 'react'
import { GoogleMapProvider } from 'app/components/map/GoogleMapProvider'
import { isNotDefined, isNotEmpty, isTrue, type LatLng, type LockableTeamField, type TeamData } from '@terros/common'
import { Text } from '@mantine/core'
import {
  LOCKED_FIELD_MESSAGE,
  useLockedTeamFields,
  usePermissionCheck,
  usePreference,
  useProfile,
  useTranslation,
} from '@front/model'
import { type SelectedPlace } from '../editTeam/types'
import { OfficeAddressInput } from '../editTeam/OfficeAddressInput'
import { type OptionalRowEdit, type OptionalRowItem } from '../../OptionalRow'
import { useToggleTeamField } from './useToggleTeamField'
import { useTeamLevelRows } from './useTeamLevelRows'
import { useEditTeamField, type TeamFieldInputProps } from './useEditTeamField'

type Props = {
  team: TeamData
  onEditTeam: VoidFunction
}

const orEmpty = (value: string | undefined, empty: string): ReactNode =>
  isNotEmpty(value) ? value : <Text c='dimmed'>{empty}</Text>

const toOfficeLocation = (place: SelectedPlace | undefined, officeAddress: string | undefined): LatLng | undefined => {
  if (isNotDefined(place)) return
  if (place.officeAddress !== officeAddress) return
  return { latitude: place.latitude, longitude: place.longitude }
}

export const useTeamDetailRows = ({ team, onEditTeam }: Props): OptionalRowItem[] => {
  const { t } = useTranslation()
  const { company } = useProfile()
  const { data: restrictionsEnabled } = usePreference('enableVisibilityRestrictions')
  const { data: canUpdate, isLoading: isCheckingPermission } = usePermissionCheck({
    permissionType: 'team:update',
    targetId: team.teamId,
  })
  const { data: canUpdateCompany } = usePermissionCheck({
    permissionType: 'company:update',
    targetId: company?.companyId,
  })
  const cannotUpdate = team.isDeleted || isCheckingPermission || !canUpdate
  const { lockedFields, isLoading: isLoadingLocks } = useLockedTeamFields(team.teamId)
  const isLocked = (field: LockableTeamField): boolean => lockedFields.includes(field)
  const lockedTooltip = (field: LockableTeamField): string | undefined =>
    isLocked(field) ? t(LOCKED_FIELD_MESSAGE) : undefined
  const [selectedPlace, setSelectedPlace] = useState<SelectedPlace>()
  const levelRows = useTeamLevelRows({ team, disabled: cannotUpdate })

  const renderAddressInput = ({ label, value, error, onChange }: TeamFieldInputProps): ReactNode => (
    <GoogleMapProvider>
      <OfficeAddressInput
        aria-label={label}
        autoFocus
        error={error}
        flex={1}
        label={undefined}
        miw={0}
        value={value ?? ''}
        withClearButton={false}
        onAddressChange={onChange}
        onPlaceSelected={setSelectedPlace}
      />
    </GoogleMapProvider>
  )

  const officeAddressEdit = useEditTeamField(team, {
    label: 'Office Address',
    lockField: 'officeAddress',
    getValue: ({ officeAddress }) => officeAddress,
    toUpdates: (officeAddress) => ({ officeAddress, officeLocation: toOfficeLocation(selectedPlace, officeAddress) }),
    renderInput: renderAddressInput,
  })

  const externalIdEdit = useEditTeamField(team, {
    label: 'External ID',
    lockField: 'externalId',
    getValue: ({ externalId }) => externalId,
    toUpdates: (externalId) => ({ externalId }),
  })

  const closerBoundaryToggle = useToggleTeamField(team, {
    label: 'Use Closers only in this team',
    disabled: cannotUpdate,
    getValue: ({ isCloserBoundary }) => isCloserBoundary,
    toUpdates: (isCloserBoundary) => ({ isCloserBoundary }),
  })
  const restrictVisibilityToggle = useToggleTeamField(team, {
    label: 'Restrict Team',
    disabled: cannotUpdate || !canUpdateCompany,
    getValue: ({ restrictVisibility }) => restrictVisibility,
    toUpdates: (restrictVisibility) => ({ restrictVisibility }),
  })

  const descriptionEdit: OptionalRowEdit = {
    type: 'edit-step',
    onEdit: onEditTeam,
    disabled: cannotUpdate || isLoadingLocks || isLocked('description'),
  }
  const restrictTeamRows: OptionalRowItem[] = restrictionsEnabled
    ? [
        {
          label: t('Restrict Team'),
          tooltip: t(
            'Allow configuring certain permissions to be confined within this team. This will also cause the team to act as a closer boundary.'
          ),
          value: isTrue(team.restrictVisibility),
          edit: restrictVisibilityToggle,
        },
      ]
    : []

  return [
    ...levelRows,
    {
      label: t('Office Address'),
      tooltip: lockedTooltip('officeAddress'),
      value: orEmpty(team.officeAddress, t('No address')),
      edit: officeAddressEdit,
    },
    {
      label: t('Description'),
      tooltip: lockedTooltip('description'),
      value: orEmpty(team.description, t('No description')),
      edit: descriptionEdit,
    },
    {
      label: t('External ID'),
      tooltip: lockedTooltip('externalId'),
      value: orEmpty(team.externalId, t('No external ID')),
      canCopy: isNotEmpty(team.externalId),
      copyValue: team.externalId,
      edit: externalIdEdit,
    },
    {
      label: t('Use Closers only in this team'),
      tooltip: t('Closer Events for Setters in this team should be restricted to Closers only in this team'),
      value: isTrue(team.isCloserBoundary),
      edit: closerBoundaryToggle,
    },
    ...restrictTeamRows,
  ]
}
